import React, { useState, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import SupplierEventDetailV2 from '../../components/SupplierEventDetailV2';
import api from '../../services/api';
import { toast } from 'sonner';
import { Eye, ChevronLeft, Search, Info } from 'lucide-react';
import { PORTAL_API } from './constants';
import { SortableTableHeader, useSortState, sortData } from '../../components/SortableTableHeader';

const TZ_DEFAULT = 'America/Los_Angeles';

function getUrgencyColor(closingDate) {
  if (!closingDate) return { bg: '#e2e8f0', pct: 0, label: 'No closing date' };
  const now = Date.now();
  const close = new Date(closingDate).getTime();
  const remaining = close - now;
  if (remaining <= 0) return { bg: '#ef4444', pct: 100, label: 'Expired' };
  const days = Math.ceil(remaining / (24 * 3600 * 1000));
  const totalWindow = 30 * 24 * 3600 * 1000;
  const pct = Math.min(100, Math.max(0, ((totalWindow - remaining) / totalWindow) * 100));
  const label = days <= 1 ? 'Closing today' : days <= 3 ? `Closing in ${days} days — urgent` : `Closing in ${days} days`;
  if (pct < 40) return { bg: `hsl(${120 - pct * 1.5}, 70%, 45%)`, pct, label };
  if (pct < 70) return { bg: `hsl(${60 - (pct - 40) * 2}, 80%, 45%)`, pct, label };
  return { bg: `hsl(${0 + Math.max(0, 20 - (pct - 70) * 0.7)}, 85%, 45%)`, pct, label };
}

const TYPE_TOOLTIPS = {
  RFI: 'Request for Information — You are being asked to provide information about your company and capabilities. No pricing required.',
  RFQ: 'Request for Quotation — You are being asked to submit your best pricing for specific items or services.',
  RFP: 'Request for Proposal — You are being asked to submit a detailed proposal covering your approach, experience, and pricing.',
  REVERSE_AUCTION: 'Reverse Auction — You are invited to compete in a live, real-time bidding event.',
};

export function SupplierRfxPublicTab({ token }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [eventDetail, setEventDetail] = useState(null);
  const authH = { headers: { Authorization: `Bearer ${token}` } };

  useEffect(() => {
    const load = async () => {
      try {
        const r = await api.get(`${PORTAL_API}/public-events`, authH);
        setEvents(r.data.data || r.data || []);
      } catch {
        // Fallback: try the tenders API
        try {
          const r2 = await api.get('/api/rfx/public-tenders');
          setEvents(r2.data.data || r2.data || []);
        } catch { }
      }
      setLoading(false);
    };
    load();
  }, []); // eslint-disable-line

  const openEvent = async (ref) => {
    try {
      const r = await api.get(`${PORTAL_API}/event/${ref}`, authH);
      setEventDetail(r.data);
      setSelectedEvent(ref);
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to load event');
    }
  };

  if (selectedEvent && eventDetail) {
    return <SupplierEventDetailV2 event={eventDetail} token={token} PORTAL_API={PORTAL_API} onBack={() => { setSelectedEvent(null); setEventDetail(null); }} />;
  }

  const now = Date.now();
  const openEvents = events.filter(e => e.status === 'OPEN' && (!e.closing_date || new Date(e.closing_date).getTime() > now));
  const closedEvents = events.filter(e => e.status !== 'OPEN' || (e.closing_date && new Date(e.closing_date).getTime() <= now));
  const byType = (list) => {
    const groups = {};
    for (const e of list) {
      const t = e.type || 'OTHER';
      if (!groups[t]) groups[t] = [];
      groups[t].push(e);
    }
    return groups;
  };

  return (
    <div>
      <div className="mb-3">
        <h2 className="text-lg font-bold">RFx Public View</h2>
        <p className="text-xs text-slate-500">Browse all publicly listed sourcing events. Click any row to view details and respond.</p>
      </div>

      {loading ? (
        <p className="text-sm text-slate-400 text-center py-8">Loading events...</p>
      ) : events.length === 0 ? (
        <p className="text-sm text-slate-400 text-center py-8">No public sourcing events available at this time.</p>
      ) : (
        <div className="space-y-6">
          {Object.entries(byType(openEvents)).map(([type, items]) => (
            <div key={type}>
              <div className="flex items-center gap-2 mb-2">
                <h3 className="font-bold text-sm">Currently Open {type === 'REVERSE_AUCTION' ? 'Reverse Auctions' : `${type}s`}</h3>
                <span className="bg-indigo-100 text-indigo-700 text-xs font-bold px-2 py-0.5 rounded-full">{items.length}</span>
                {TYPE_TOOLTIPS[type] && (
                  <div className="relative group">
                    <Info className="w-3.5 h-3.5 text-slate-400 cursor-help" />
                    <div className="absolute left-0 top-full mt-1 z-50 w-80 p-3 bg-slate-900 text-white text-xs rounded-lg shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-colors pointer-events-none leading-relaxed">{TYPE_TOOLTIPS[type]}</div>
                  </div>
                )}
              </div>
              <div className="border rounded-lg bg-white overflow-hidden">
                <table className="w-full text-sm">
                  <thead><tr className="bg-slate-50 border-b">
                    <th className="text-left px-3 py-2 font-semibold">Reference</th>
                    <th className="text-left px-3 py-2 font-semibold min-w-[200px]">Description</th>
                    <th className="text-left px-3 py-2 font-semibold">Status</th>
                    <th className="text-left px-3 py-2 font-semibold">Issued</th>
                    <th className="text-left px-3 py-2 font-semibold min-w-[200px]">Closing Date</th>
                  </tr></thead>
                  <tbody>
                    {items.map(ev => {
                      const urgency = getUrgencyColor(ev.closing_date);
                      return (
                      <tr key={ev.event_ref} className={`border-b cursor-pointer transition-colors ${ev.has_responded ? 'bg-emerald-50 hover:bg-emerald-100' : 'hover:bg-blue-50'}`}
                        onClick={() => openEvent(ev.event_ref)} onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openEvent(ev.event_ref); } }}
                        tabIndex={0} role="row" aria-label={`${ev.event_ref} — ${ev.title}`}>
                        <td className="px-3 py-2.5 font-mono font-semibold text-indigo-700">{ev.event_ref}</td>
                        <td className="px-3 py-2.5 max-w-[300px] truncate">{ev.title}</td>
                        <td className="px-3 py-2.5">{ev.has_responded ? <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">Responded</Badge> : <Badge className="bg-slate-100 text-slate-600 text-[10px]">Open</Badge>}</td>
                        <td className="px-3 py-2.5 text-xs">{(ev.start_date || ev.created_at) ? new Date(ev.start_date || ev.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', timeZone: TZ_DEFAULT }) : '—'}</td>
                        <td className="px-3 py-0">
                          <div className="relative rounded-md overflow-hidden" style={{ backgroundColor: urgency.bg + '20' }}>
                            <div className="absolute left-0 top-0 bottom-0 rounded-md" style={{ width: `${urgency.pct}%`, backgroundColor: urgency.bg, opacity: 0.25 }} />
                            <span className="relative z-10 block px-3 py-2.5 font-semibold text-xs" style={{ color: urgency.bg }}>
                              {ev.closing_date ? new Date(ev.closing_date).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: TZ_DEFAULT }) : '—'}
                            </span>
                          </div>
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          ))}

          {closedEvents.length > 0 && Object.entries(byType(closedEvents)).map(([type, items]) => (
            <div key={`closed-${type}`}>
              <h3 className="font-bold text-sm mb-2 text-slate-500">Closed {type === 'REVERSE_AUCTION' ? 'Reverse Auctions' : `${type}s`} <span className="bg-slate-200 text-slate-600 text-xs font-bold px-2 py-0.5 rounded-full ml-1">{items.length}</span></h3>
              <div className="border rounded-lg bg-white overflow-hidden">
                <table className="w-full text-sm text-slate-500">
                  <thead><tr className="bg-slate-50 border-b">
                    <th className="text-left px-3 py-2 font-semibold">Reference</th>
                    <th className="text-left px-3 py-2 font-semibold">Description</th>
                    <th className="text-left px-3 py-2 font-semibold">Closed Date</th>
                  </tr></thead>
                  <tbody>
                    {items.map(ev => (
                      <tr key={ev.event_ref} className="border-b">
                        <td className="px-3 py-2 font-mono">{ev.event_ref}</td>
                        <td className="px-3 py-2">{ev.title}</td>
                        <td className="px-3 py-2 text-xs">{ev.closing_date ? new Date(ev.closing_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', timeZone: TZ_DEFAULT }) : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function SupplierMyRfxTab({ myRfx, token, onOpenEvent, selectedEvent, eventDetail, onBack }) {
  const [sortState, handleSort] = useSortState('event_ref', 'desc');
  const relColors = {
    INVITED_NOT_RESPONDED: 'bg-amber-100 text-amber-700',
    RESPONDED: 'bg-emerald-100 text-emerald-700',
    BID_SUBMITTED: 'bg-blue-100 text-blue-700',
    EXPRESSED_INTEREST: 'bg-slate-100 text-slate-600',
  };

  // Show event detail view when an event is selected
  if (selectedEvent && eventDetail) {
    return <SupplierEventDetailV2 event={eventDetail} token={token} PORTAL_API={PORTAL_API} onBack={onBack} />;
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-bold">My RFx</h2>
          <p className="text-xs text-slate-500">Events you've been invited to, responded to, or bid on. Click any row to view details.</p>
        </div>
        <span className="text-xs text-slate-500">{myRfx.length} event(s)</span>
      </div>
      {myRfx.length === 0 ? <p className="text-sm text-slate-400 text-center py-8">You don't have any direct RFx relationships yet. Browse the public view to find opportunities.</p> : (
        <div className="border rounded-lg bg-white overflow-hidden">
          <table className="w-full text-sm">
            <thead><tr className="bg-slate-50 border-b">
              <SortableTableHeader label="Reference" field="event_ref" sortState={sortState} onSort={handleSort} />
              <SortableTableHeader label="Type" field="type" sortState={sortState} onSort={handleSort} />
              <SortableTableHeader label="Description" field="title" sortState={sortState} onSort={handleSort} className="min-w-[200px]" />
              <SortableTableHeader label="Issued" field="start_date" sortState={sortState} onSort={handleSort} />
              <SortableTableHeader label="Responded" field="responded_at" sortState={sortState} onSort={handleSort} />
              <SortableTableHeader label="Status" field="status" sortState={sortState} onSort={handleSort} />
              <SortableTableHeader label="Our Status" field="relationship_label" sortState={sortState} onSort={handleSort} />
            </tr></thead>
            <tbody>
              {sortData(myRfx, sortState.field, sortState.dir).map(ev => (
                <tr key={ev.event_ref} className="border-b hover:bg-blue-50 cursor-pointer"
                  onClick={() => onOpenEvent(ev.event_ref)} onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpenEvent(ev.event_ref); } }}
                  tabIndex={0} role="row" aria-label={`${ev.event_ref} — ${ev.title}`}>
                  <td className="px-3 py-2 font-semibold text-indigo-700">{ev.event_ref}</td>
                  <td className="px-3 py-2"><Badge className={`text-[10px] ${ev.type === 'REVERSE_AUCTION' ? 'bg-rose-100 text-rose-700' : 'bg-indigo-100 text-indigo-700'}`}>{ev.type === 'REVERSE_AUCTION' ? 'Auction' : ev.type}</Badge></td>
                  <td className="px-3 py-2 max-w-[300px] truncate" title={ev.title}>{ev.title}</td>
                  <td className="px-3 py-2 text-xs">{ev.start_date ? new Date(ev.start_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', timeZone: TZ_DEFAULT }) : '—'}</td>
                  <td className="px-3 py-2 text-xs">{ev.responded_at ? new Date(ev.responded_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', timeZone: TZ_DEFAULT }) : ev.latest_bid_at ? new Date(ev.latest_bid_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', timeZone: TZ_DEFAULT }) : '—'}</td>
                  <td className="px-3 py-2"><Badge className="text-[10px] bg-slate-100 text-slate-600">{ev.status}</Badge></td>
                  <td className="px-3 py-2"><Badge className={`text-[10px] ${relColors[ev.relationship] || 'bg-slate-100'}`}>{ev.relationship_label}</Badge></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
